import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { Status, StatusType, useChainData } from 'modules/common';
import { globalTranslation, useTranslation } from 'modules/i18n';
import { TaskRoutesConfig } from 'modules/task';

import { GetTasksItem } from '../../actions/getTasks';

interface Props {
  task: GetTasksItem;
}

export function ExpandedTask({ task }: Props): JSX.Element {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { t, keys } = useTranslation(globalTranslation);
  const chainData = useChainData(task.chainId);

  return (
    <div
      data-tid={`dashboard-grouped-tasks-item-${task.id}`}
      className={classes.row}
      onClick={event => {
        event.preventDefault();
        if (chainData?.chainUnit) {
          navigate(
            TaskRoutesConfig.editTask.generatePath(
              task.id,
              chainData.chainUnit,
            ),
          );
        }
      }}
    >
      <div className={classes.name}>
        <Typography
          variant="subtitle3"
          sx={theme => ({
            color: theme.palette.primary.main,
            wordBreak: 'break-all',
          })}
        >
          {task.name}
        </Typography>
      </div>

      <div className={classes.taskStatus}>
        <Status isError={task.isPaused} type={StatusType.Task} />
      </div>

      <div className={classes.taskAmount}>
        <Typography variant="subtitle3">
          {t(keys.units.tokenAmount, {
            amount: task.amountSpent.toString(),
            token: chainData?.tokenName,
          })}
        </Typography>
      </div>
    </div>
  );
}

export const useStyles = makeStyles()(theme => ({
  row: {
    display: 'grid',
    gridTemplateRows: '1fr',
    gridTemplateColumns: `1fr 20% 30%`,
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(6, 0),

    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: `1fr 21% 30%`,
    },

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(4),
      gridTemplateRows: '1fr 1fr',
      gridTemplateColumns: `1fr 1fr`,
      padding: theme.spacing(3, 0),
    },

    '&:last-of-type': {
      paddingBottom: 0,
    },
  },

  name: {
    paddingLeft: 68,

    [theme.breakpoints.down('md')]: {
      paddingLeft: 0,
    },
  },

  taskStatus: {
    display: 'flex',
    width: 'auto',
    padding: theme.spacing(0, 3),

    [theme.breakpoints.down('md')]: {
      padding: 0,
    },

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-end',
    },
  },
  taskAmount: {
    textAlign: 'right',

    [theme.breakpoints.down('sm')]: {
      gridColumn: `span 2`,
    },
  },
}));
